"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { signOut } from "next-auth/react";
import { Link } from "@/i18n/navigation";
import type { NavUser } from "./NavBar";

function initials(name: string | null, email: string | null): string {
  if (name?.trim()) {
    const parts = name.trim().split(/\s+/);
    return (parts[0][0] + (parts[1]?.[0] ?? "")).toUpperCase();
  }
  return (email?.[0] ?? "?").toUpperCase();
}

// Top-bar account menu: avatar + name, with a dropdown showing the email and
// account / admin / logout actions. Closes on outside click or Escape.
export function UserMenu({ user }: { user: NonNullable<NavUser> }) {
  const t = useTranslations("Nav");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const itemClass =
    "block w-full rounded-lg px-3 py-2 text-left text-sm text-bark transition-colors hover:bg-forest-50";

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-full border border-sand bg-cream py-1 pl-1 pr-2.5 transition-colors hover:border-forest-300"
      >
        <span className="grid h-8 w-8 place-items-center rounded-full bg-forest-500 text-xs font-semibold text-white">
          {initials(user.name, user.email)}
        </span>
        <span className="hidden max-w-[10rem] truncate text-sm font-medium text-bark sm:block">
          {user.name ?? user.email}
        </span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden
          className={`text-stone transition-transform ${open ? "rotate-180" : ""}`}
        >
          <path
            d="M6 9l6 6 6-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 w-64 overflow-hidden rounded-2xl border border-sand bg-cream shadow-lg"
        >
          <div className="border-b border-sand px-4 py-3">
            <p className="truncate text-sm font-semibold text-bark">
              {user.name ?? t("account")}
            </p>
            {user.email && (
              <p className="truncate text-xs text-stone">{user.email}</p>
            )}
          </div>
          <div className="p-1">
            {user.role === "ADMIN" && (
              <Link
                href="/admin"
                role="menuitem"
                onClick={() => setOpen(false)}
                className={itemClass}
              >
                {t("admin")}
              </Link>
            )}
            <Link
              href="/account"
              role="menuitem"
              onClick={() => setOpen(false)}
              className={itemClass}
            >
              {t("account")}
            </Link>
            <button
              role="menuitem"
              onClick={() => signOut({ callbackUrl: "/" })}
              className={`${itemClass} font-medium text-red-600 hover:bg-red-50`}
            >
              {t("logout")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
